import { useContext, useEffect, useRef, useState } from "react";
import { findPricingViaIdHelper, getUrlQuerysection } from "../../../helpers";
import { useSearchParams } from "react-router-dom";
import useScanDetection from "use-scan-detection";
import { DEFAULT_PRICING_ID } from "../../../helpers/constants";
import { ToastContext } from "../../../hooks/context";
import { useGetAllProductsList } from "../../../hooks/api/queries";

export const useBulkRestockComboBox = ({
  handleAppendRestockData,
  exclude_on_demand_composite_products = false,
  store_id = null,
  setStoreError = () => {},
}) => {
  const [search, setSearch] = useSearchParams();
  const queries = getUrlQuerysection();
  const [queryParams, setQueryParams] = useState({
    exclude_on_demand_composite_products,
  });
  const searchRef = useRef(null);
  const [isDropDown, setIsDropDown] = useState(false);
  const [isVariantOpenId, setIsVariantOpenId] = useState(null);
  const [searchValue] = useState(search.get("query") || "");
  const triggerToast = useContext(ToastContext);

  const { isLoading, data } = useGetAllProductsList({
    queries,
    queryParams,
    enabled: queryParams?.store_id ? true : false,
    onSuccess: (res) => {
      if (search.get("is_scan") === "true") {
        search.delete("is_scan");
        setSearch(search, { replace: true });
        const product = res?.[0];
        if (!product) return;
        const variants =
          product?.variants?.length > 0 ? product?.variants?.[0] : null;
        if (!variants) addProductForRestock(product);
        else setIsVariantOpenId(product?.id);
      }
    },
  });

  const searchProduct = (value, isScanBar = false) => {
    if (!store_id) {
      const message = "Please select a store";
      setStoreError(message);
      triggerToast(message, "warning");
      setTimeout(() => {
        setStoreError("");
      }, 5000);

      return;
    }

    if (value?.length === 0) {
      search.delete("query");
      search.delete("is_scan");
      setIsDropDown(false);
    } else {
      setIsDropDown(true);
      search.set("query", value);
      search.set("is_scan", isScanBar);
    }
    setSearch(search, { replace: true });
  };

  const addProductForRestock = (product, variant = []) => {
    const isVariant = product?.variants?.length > 0;

    handleAppendRestockData({
      stock_quantity: isVariant
        ? variant?.store_variant_properties?.[0]?.stock_quantity
        : product?.store_product_properties?.[0]?.stock_quantity,
      quantity: "",
      name: isVariant
        ? `${product?.name} (${variant?.option} - ${variant?.value})`
        : product?.name,
      product_id: product?.id,
      variant_id: isVariant ? variant?.id : undefined,
      variant: isVariant ? variant?.value : undefined,
      store_id: isVariant
        ? variant?.store_variant_properties?.[0]?.store_id
        : product?.store_product_properties?.[0]?.store_id,
      store_name: isVariant
        ? variant?.store_variant_properties?.[0]?.store_name
        : product?.store_product_properties?.[0]?.store_name,
      barcode: isVariant
        ? `${variant?.barcode ? variant?.barcode : ""}`
        : `${product?.barcode ? product?.barcode : ""}`,
      tracking_number: isVariant
        ? `${variant?.tracking_number || ""}`
        : `${product?.tracking_number || ""}`,
      expiry_date: isVariant
        ? `${
            variant?.store_variant_properties?.[0]?.expiry_date
              ? variant?.store_variant_properties?.[0]?.expiry_date
              : ""
          }`
        : `${
            product?.store_product_properties?.[0]?.expiry_date
              ? product?.store_product_properties?.[0]?.expiry_date
              : ""
          }`,
      cost_price: isVariant
        ? variant?.store_variant_properties?.[0]?.cost_price
        : product?.store_product_properties?.[0]?.cost_price,
      product_type: isVariant
        ? "Variant"
        : product?.is_composite
          ? "Composite"
          : "Single Product",
      pricings: [
        findPricingViaIdHelper(
          DEFAULT_PRICING_ID,
          isVariant
            ? variant?.store_variant_properties?.[0]?.pricings
            : product?.store_product_properties?.[0]?.pricings,
        ),
      ],
      unit: product?.unit?.unit,
      vat_rate:
        product?.vat ||
        product?.stores.find((store) => store.id === parseInt(store_id))?.vat,
    });
    setIsDropDown(false);
    search.delete("is_scan");
    search.delete("query");
    setSearch(search, { replace: true });
  };

  const handleScanBar = (value) => {
    searchProduct(value, true);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target))
        setIsDropDown(false);
    };
    document.addEventListener("click", handleClickOutside);
  }, [searchRef]);

  useEffect(() => {
    setQueryParams((prev) => ({
      ...prev,
      store_id: store_id || search.get("store_id"),
    }));
  }, [store_id]);

  useScanDetection({
    onComplete: (value) => {
      const activeEl = document.activeElement?.tagName;
      if (activeEl === "INPUT" || activeEl === "TEXTAREA") return;
      const cleanedValue = value
        // eslint-disable-next-line no-control-regex
        ?.replace(/[\x00-\x1F\x7F]/g, "")
        ?.replace(
          /ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Backspace|Delete/g,
          "",
        )
        ?.trim();

      if (cleanedValue) handleScanBar(cleanedValue);
    },
    minLength: 8,
  });

  return {
    searchRef,
    isLoading,
    products: data?.data || [],
    isDropDown,
    searchValue,
    isVariantOpenId,
    searchProduct,
    setIsDropDown,
    addProductForRestock,
    setIsVariantOpenId,
  };
};
